import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { gsap } from "gsap";

/**
  Use when an inline toggle is desired.
 */

const InlineToggle = ({
  options,
  selectedValue,
  classList = "",
  animatedOption = "",
  AnimatedIcon = () => {},
  toggleDisabled = false,
  sendOnChange
}) => {
  const [disabled, setDisabled] = useState(toggleDisabled);
  const [value, setValue] = useState(selectedValue);

  const slider = useRef(null);

  const calcXPercent = (index) => {
    const selectOptionsCount = options.length;
    const slideWidthPercent = parseInt(
      getComputedStyle(slider.current).getPropertyValue("--slider-width"),
      10
    );
    let xPercent;

    switch (parseInt(index, 10)) {
      case selectOptionsCount - 1:
        xPercent = 100 - slideWidthPercent - 1.5;
        break;
      case 1:
        xPercent = 50 - slideWidthPercent / 2 - 1.5;
        break;
      default:
        xPercent = 0;
    }

    return xPercent;
  };

  const toggleSlider = (index, element) => {
    const frequencyAnimation = gsap.timeline({
      autoRemoveChildren: true,
      paused: true
    });

    const slideAnimation = () => {
      const slide = gsap.timeline();
      const xPercent = calcXPercent(index);

      slide.to(element, {
        duration: 0.2,
        xPercent,
        ease: "power2.in"
      });

      return slide;
    };

    frequencyAnimation.addLabel("start").add(slideAnimation().paused(false), "start");

    return frequencyAnimation;
  };

  // Set initial xpercent based on selected value
  useEffect(() => {
    // get index of selected value from options
    const initialIndex = options.findIndex((option) => Object.values(option)[0] === selectedValue);

    const xPercent = calcXPercent(initialIndex);
    const timeline = gsap.timeline();
    timeline.set(slider.current, { xPercent }).to(slider.current, { duration: 0.2, autoAlpha: 1 });
  }, []);

  const handleOnChange = (e) => {
    const index = e.target.attributes.index.value;
    setValue(e.target.value);
    sendOnChange(e);
    const animation = toggleSlider(index, slider.current);
    animation.play();
  };

  /**
   *
   * @returns
   */
  const renderToggleOptions = () => {
    return options.map((option, index) => {
      const optionKey = Object.keys(option)[0];
      const optionValue = Object.values(option)[0];
      let toggleSwitchIcon;

      if (
        options.length < 3 &&
        animatedOption &&
        animatedOption === value &&
        optionValue === animatedOption
      ) {
        toggleSwitchIcon = <AnimatedIcon />;
      }
      return (
        <div className="toggle-switch__option" key={optionKey}>
          <input
            className="toggle-switch__input"
            id={optionValue}
            index={index}
            name="neo"
            value={optionValue}
            type="radio"
            defaultChecked={optionValue === value}
            disabled={toggleDisabled}
            onChange={(event) => handleOnChange(event)}
          />
          <label className="toggle-switch__label" htmlFor={optionValue}>
            {toggleSwitchIcon}
            {optionKey}
          </label>
        </div>
      );
    });
  };

  const disabledClass = disabled ? "disabled" : "";

  const threeAcrossClass = options.length === 3 ? "toggle-switch--inline--three" : "";
  // Hiding toggle until initial position is set
  const style = { opacity: 0 };

  return (
    <div
      className={`toggle-switch toggle-switch--neo toggle-switch--inline ${classList} ${disabledClass} ${threeAcrossClass}`}
    >
      {renderToggleOptions()}
      <span className="toggle-switch__slider" ref={slider} style={style} />
    </div>
  );
};

InlineToggle.propTypes = {
  /** Pass 2 or 3 objects with labels and values */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Pass the value of the default option */
  selectedValue: PropTypes.string.isRequired,
  classList: PropTypes.string,
  /** Toggle option that has an animation when selected */
  animatedOption: PropTypes.string,
  /** Font awesome icon to use for selected animation */
  AnimatedIcon: PropTypes.func,
  /** Disable the toggle */
  toggleDisabled: PropTypes.bool,
  /** This function sends an onchange event back to the parent element. */
  sendOnChange: PropTypes.func.isRequired
};

export default InlineToggle;
